import SearchIcon from "@mui/icons-material/Search";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import { ErrorBoundary } from "@sentry/react";
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import { ErrorMsg } from "../../components/AppMessages";
import { CedarShellLayout } from "../../components/Layouts";
import { dashboardMatchesSearchTerm, SearchableDashboard } from "../../constants";
import { useDashboardSearchTracking } from "../../hooks";
import { useDashboardCategories } from "../../hooks/react-query/dashboards/useDashboardCategories";
import { useDashboardRegistry } from "../../hooks/react-query/dashboards/useDashboardRegistry";
import { CollapsibleDashboardGroup } from "./components/CollapsibleDashboardGroup";
import "./dashboardsPage.scss";

export const DashboardsPage = () => {
  const { dashboards } = useDashboardRegistry();
  // GET /dashboards/dashboard_category_list is live (docs/needed-endpoints.md §11) - the
  // canonical, authoritative category list (every category that exists, whether or not any
  // dashboard is in it yet). Category creation/dashboard-assignment admin actions have been
  // removed from the frontend - this page only ever reads/groups by category now.
  //
  // "Archived" (slug "archived") is excluded here regardless of what's assigned to it - it's an
  // organizational category some dashboards were seeded under (still is_active: true, per
  // docs/dashboard-registry-seed-checklist.md's own "is_active ≠ archived" note), NOT the same
  // concept as this page's own is_active-based archived exclusion below. Archived dashboards -
  // either sense of the word - should only ever be viewable on the dedicated /archived page
  // (ArchivedPage.tsx), not show up as a group here too.
  const { categories: allCategories } = useDashboardCategories();
  const CATEGORIES = allCategories.filter((category) => category.slug !== "archived");
  const [searchTerm, setSearchTerm] = useState("");

  // Categories default open (this is the primary browse view). The registry loads
  // asynchronously, so categories are merged in as they appear rather than seeded once
  // synchronously - this only ever adds newly-seen keys, never clobbering a toggle the user's
  // already made.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedGroups((prev) => {
      const missing = CATEGORIES.filter((category) => !(category.name in prev));
      if (missing.length === 0) return prev;
      const next = { ...prev };
      missing.forEach((category) => {
        next[category.name] = false;
      });
      return next;
    });
  }, [CATEGORIES]);

  const toggleGroup = (key: string) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const trimmedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (dashboard: SearchableDashboard) => dashboardMatchesSearchTerm(dashboard, trimmedSearch);

  // Categories sorted alphabetically by name; each group's own dashboards sorted alphabetically
  // too, rather than relying on whatever order the registry/category-list endpoints happen to
  // return.
  const categoryGroups = useMemo(
    () =>
      CATEGORIES.map((category) => ({
        category,
        dashboards: dashboards
          // is_active now doubles as "archived" (dashboardsApi.ts's own getDashboardRegistry
          // comment) - excluded here so an archived dashboard doesn't show up mixed in with
          // active ones under whatever category it was last assigned to; it belongs on the
          // dedicated /archived page instead (ArchivedPage.tsx).
          .filter((dashboard) => dashboard.is_active && dashboard.categories.some((c) => c.id === category.id) && matchesSearch(dashboard))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
        .filter(
          (group) =>
            group.dashboards.length > 0 ||
            // Every real category stays visible with 0 dashboards - except while actively searching,
            // since an empty group can never match a search term.
            !trimmedSearch
        )
        .sort((a, b) => a.category.name.localeCompare(b.category.name)),
    [trimmedSearch, dashboards, CATEGORIES]
  );

  // While searching, auto-open any group with a match rather than requiring the user to also
  // expand it manually - the user's own toggle state is preserved underneath and restored once
  // the search is cleared.
  const isGroupOpen = (key: string, hasMatches: boolean) => (trimmedSearch ? hasMatches : expandedGroups[key]);

  // Position is the card's place in the flattened render order across every open group, so a
  // result's rank is comparable with Home's own flat dropdown.
  // Distinct dashboards, not rendered cards - one assigned to two categories appears in both
  // groups but is a single result.
  const resultCount = useMemo(
    () => new Set(categoryGroups.flatMap((group) => group.dashboards.map((dashboard) => dashboard.key))).size,
    [categoryGroups]
  );

  const { trackSelection } = useDashboardSearchTracking(searchTerm, resultCount);

  const handleOpenDashboard = (dashboard: SearchableDashboard) => trackSelection(dashboard);

  return (
    <CedarShellLayout>
      <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
        <div className="dashboards_page" data-test-id="dashboards-page">
          <div className="dashboards_page_header_row">
            <SpaceDashboardOutlinedIcon className="page_header_icon" />
            <div>
              <h1 className="dashboards_page_title">Dashboards</h1>
              <p className="dashboards_page_subtitle">Dashboards relevant to your team.</p>
            </div>
          </div>

          <div className="dashboards_page_search_row">
            <div className="dashboards_page_search">
              <SearchIcon className="dashboards_page_search_icon" />
              <input
                type="text"
                value={searchTerm}
                placeholder="Search dashboards and KPIs"
                onChange={(e) => setSearchTerm(e.target.value)}
                data-test-id="dashboards-page-search-input"
              />
            </div>
          </div>

          <div className="dashboards_page_groups">
            {categoryGroups.map((group) => (
              <CollapsibleDashboardGroup
                key={group.category.id}
                label={group.category.name}
                dashboards={group.dashboards}
                isOpen={isGroupOpen(group.category.name, group.dashboards.length > 0)}
                onToggle={() => toggleGroup(group.category.name)}
                color={group.category.color}
                onOpenDashboard={handleOpenDashboard}
              />
            ))}

            {/* A search with no matches used to just silently leave this whole area empty - every
                category group disappears (each has 0 dashboards, and the "stay visible" carve-out
                only applies while NOT searching), so nothing at all indicated whether the search
                was still loading, still being typed, or genuinely had no matches. Note: a
                dashboard assigned to zero categories (if one ever exists) won't appear in
                categoryGroups at all and so can't match here either - the removed "All Dashboards"
                catch-all used to be the only thing that made such a dashboard findable via search
                on this page. */}
            {trimmedSearch && categoryGroups.length === 0 && (
              <div className="dashboards_page_no_results" data-test-id="dashboards-page-no-results">
                No dashboards or KPIs match &quot;{searchTerm.trim()}&quot;. Try a different search term.
              </div>
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          draggable={false}
          pauseOnHover={false}
        />
      </ErrorBoundary>
    </CedarShellLayout>
  );
};
